import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Crown, Building2, Clock, PoundSterling, AlertTriangle, CheckCircle2, TrendingUp, Mail, Baby, CalendarDays, Shield, FileText, ChevronRight, RefreshCw, XCircle, Bell, BellOff } from 'lucide-react';
import { subscribeToPush, unsubscribeFromPush, isSubscribed, sendTestPush, isPushSupported } from '../utils/pushNotifications';

const API = process.env.REACT_APP_BACKEND_URL;

const CeoDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/ceo/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    isPushSupported().then(setPushSupported);
    isSubscribed().then(setPushEnabled).catch(() => {});
  }, []);

  const togglePush = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
      } else {
        await subscribeToPush();
        setPushEnabled(true);
        await sendTestPush();
      }
    } catch (e) { console.error(e); alert(e.message); }
    setPushLoading(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }} />
    </div>
  );

  if (!data) return null;

  const healthScore = Math.max(0, 100 - (data.tasks.overdue * 5) - (data.hmrc.overdue * 15) - (data.hmrc.debts_with_collectors * 10));
  const healthColor = healthScore >= 80 ? '#16A34A' : healthScore >= 50 ? '#D97706' : '#DC2626';
  const healthLabel = healthScore >= 80 ? 'Healthy' : healthScore >= 50 ? 'Needs Attention' : 'Critical';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="ceo-dashboard">
      {/* Header */}
      <header className="bg-white px-6 lg:px-10 py-5" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-xs flex items-center gap-1.5 hover:opacity-70" style={{ color: '#8B7355' }} data-testid="back-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> Ops Suite
            </button>
            <div className="h-6 w-px" style={{ backgroundColor: '#E5E1DB' }} />
            <div>
              <h1 className="text-xl font-light tracking-tight" style={{ color: '#2C1A0E' }}>CEO Dashboard</h1>
              <p className="text-[10px] uppercase tracking-widest" style={{ color: '#8B7355' }}>Executive Overview — {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pushSupported && (
              <button onClick={togglePush} disabled={pushLoading} className="flex items-center gap-1.5 px-3 py-2 text-xs border rounded-sm hover:bg-amber-50 disabled:opacity-50" style={{ borderColor: '#E5E1DB', color: pushEnabled ? '#16A34A' : '#8B7355' }} data-testid="push-toggle">
                {pushEnabled ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                {pushLoading ? 'Working...' : pushEnabled ? 'Notifications On' : 'Enable Notifications'}
              </button>
            )}
            <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-2 text-xs border rounded-sm hover:bg-amber-50" style={{ borderColor: '#E5E1DB', color: '#8B7355' }} data-testid="refresh-btn">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-6">

        {/* Health Score + Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Overall Health */}
          <div className="bg-white p-5 rounded-sm border" style={{ borderColor: '#E5E1DB', borderLeft: `3px solid ${healthColor}` }} data-testid="health-score">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4" style={{ color: healthColor }} />
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#8B7355' }}>Operations Health</span>
            </div>
            <p className="text-3xl font-light" style={{ color: healthColor }}>{healthScore}%</p>
            <p className="text-xs mt-1" style={{ color: healthColor }}>{healthLabel}</p>
          </div>

          {/* Tasks */}
          <div className="bg-white p-5 rounded-sm border" style={{ borderColor: '#E5E1DB' }} data-testid="tasks-kpi">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4" style={{ color: '#8B7355' }} />
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#8B7355' }}>Tasks</span>
            </div>
            <p className="text-3xl font-light" style={{ color: '#2C1A0E' }}>{data.tasks.total_open}</p>
            <div className="flex gap-3 mt-1">
              <span className="text-xs" style={{ color: data.tasks.overdue > 0 ? '#DC2626' : '#16A34A' }}>{data.tasks.overdue} overdue</span>
              <span className="text-xs" style={{ color: '#D97706' }}>{data.tasks.due_this_week} this week</span>
            </div>
          </div>

          {/* HMRC */}
          <div className="bg-white p-5 rounded-sm border" style={{ borderColor: '#E5E1DB' }} data-testid="hmrc-kpi">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4" style={{ color: '#D4AF37' }} />
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#8B7355' }}>HMRC</span>
            </div>
            <p className="text-3xl font-light" style={{ color: data.hmrc.total_debt > 0 ? '#DC2626' : '#16A34A' }}>£{data.hmrc.total_debt.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            <div className="flex gap-3 mt-1">
              <span className="text-xs" style={{ color: '#8B7355' }}>{data.hmrc.total_deadlines_upcoming} deadlines</span>
              <span className="text-xs" style={{ color: data.hmrc.overdue > 0 ? '#DC2626' : '#16A34A' }}>{data.hmrc.overdue} overdue</span>
            </div>
          </div>

          {/* Revenue */}
          <div className="bg-white p-5 rounded-sm border" style={{ borderColor: '#E5E1DB' }} data-testid="revenue-kpi">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4" style={{ color: '#0D9488' }} />
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#8B7355' }}>Revenue (30d)</span>
            </div>
            <p className="text-3xl font-light" style={{ color: '#2C1A0E' }}>£{(data.revenue?.gross_30d || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            <p className="text-xs mt-1" style={{ color: '#8B7355' }}>{data.revenue?.txn_count || 0} transactions</p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Left: HMRC + Companies */}
          <div className="space-y-4">
            {/* HMRC Compliance */}
            <div className="bg-white rounded-sm border" style={{ borderColor: '#E5E1DB' }}>
              <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <div className="flex items-center gap-2">
                  <Crown className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} />
                  <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#B8860B' }}>HMRC Compliance</h2>
                </div>
                <button onClick={() => navigate('/hmrc')} className="text-xs px-2 py-1 rounded-sm hover:bg-amber-50" style={{ color: '#8B7355' }} data-testid="go-hmrc"><ChevronRight className="w-3 h-3" /></button>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 rounded-sm" style={{ backgroundColor: '#FAF7F2' }}>
                    <p className="text-lg font-light" style={{ color: '#2C1A0E' }}>{data.hmrc.companies_tracked}</p>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8B7355' }}>Companies</p>
                  </div>
                  <div className="text-center p-3 rounded-sm" style={{ backgroundColor: data.hmrc.overdue > 0 ? '#FEF2F2' : '#F0FDF4' }}>
                    <p className="text-lg font-light" style={{ color: data.hmrc.overdue > 0 ? '#DC2626' : '#16A34A' }}>{data.hmrc.overdue}</p>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8B7355' }}>Overdue</p>
                  </div>
                  <div className="text-center p-3 rounded-sm" style={{ backgroundColor: data.hmrc.debts_with_collectors > 0 ? '#FEF2F2' : '#F0FDF4' }}>
                    <p className="text-lg font-light" style={{ color: data.hmrc.debts_with_collectors > 0 ? '#DC2626' : '#16A34A' }}>{data.hmrc.debts_with_collectors}</p>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8B7355' }}>With Collectors</p>
                  </div>
                </div>
                {data.hmrc.next_deadline && (
                  <div className="px-3 py-2 rounded-sm" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                    <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#D97706' }}>Next Deadline</p>
                    <p className="text-sm mt-0.5" style={{ color: '#2C1A0E' }}>{data.hmrc.next_deadline.title}</p>
                    <p className="text-xs font-mono" style={{ color: '#8B7355' }}>{data.hmrc.next_deadline.due_date}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Email Accounts Status */}
            <div className="bg-white rounded-sm border" style={{ borderColor: '#E5E1DB' }}>
              <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
                  <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#B8860B' }}>Email Accounts</h2>
                </div>
                <button onClick={() => navigate('/email')} className="text-xs px-2 py-1 rounded-sm hover:bg-amber-50" style={{ color: '#8B7355' }}><ChevronRight className="w-3 h-3" /></button>
              </div>
              <div className="p-4 space-y-2">
                {data.email_accounts?.map((ea, i) => (
                  <div key={ea.email} className="flex items-center justify-between py-1.5" style={{ borderBottom: i < data.email_accounts.length - 1 ? '1px solid #F5F1EB' : 'none' }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: '#16A34A' }} />
                      <span className="text-xs truncate" style={{ color: '#2C1A0E' }}>{ea.email}</span>
                      {ea.provider === 'imap' && <span className="text-[9px] px-1 rounded" style={{ backgroundColor: '#FFFBEB', color: '#D97706' }}>IMAP</span>}
                    </div>
                    <span className="text-[10px] flex-shrink-0" style={{ color: '#16A34A' }}>Connected</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Overdue Tasks */}
            {data.tasks.overdue_list?.length > 0 && (
              <div className="bg-white rounded-sm border" style={{ borderColor: '#FECACA' }}>
                <div className="px-5 py-3" style={{ borderBottom: '1px solid #FECACA' }}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                    <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#DC2626' }}>Overdue Tasks</h2>
                  </div>
                </div>
                <div className="p-4 space-y-2">
                  {data.tasks.overdue_list.map((t, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <p className="text-xs truncate" style={{ color: '#2C1A0E' }}>{t.title}</p>
                      <span className="text-[10px] font-mono flex-shrink-0 ml-2" style={{ color: '#DC2626' }}>{t.due_date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Personal + Watchlist */}
          <div className="space-y-4">
            {/* Personal Next 7 Days */}
            <div className="bg-white rounded-sm border" style={{ borderColor: '#E5E1DB' }}>
              <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-3.5 h-3.5" style={{ color: '#EC4899' }} />
                  <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#B8860B' }}>Next 7 Days — Personal</h2>
                </div>
                <button onClick={() => navigate('/planner')} className="text-xs px-2 py-1 rounded-sm hover:bg-amber-50" style={{ color: '#8B7355' }}><ChevronRight className="w-3 h-3" /></button>
              </div>
              <div className="p-4">
                {data.personal_next_7_days?.length > 0 ? (
                  <div className="space-y-2">
                    {data.personal_next_7_days.map((p, i) => (
                      <div key={i} className="flex items-center gap-3 py-1.5" style={{ borderBottom: i < data.personal_next_7_days.length - 1 ? '1px solid #F5F1EB' : 'none' }}>
                        <div className="w-10 text-center flex-shrink-0">
                          <p className="text-sm font-light" style={{ color: '#2C1A0E' }}>{new Date(p.date + 'T00:00:00').getDate()}</p>
                          <p className="text-[9px] uppercase" style={{ color: '#8B7355' }}>{new Date(p.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' })}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs truncate" style={{ color: '#2C1A0E' }}>{p.title}</p>
                          <p className="text-[10px]" style={{ color: '#8B7355' }}>{p.category} · {p.subcategory}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-center py-4" style={{ color: '#8B7355' }}>No personal items in the next 7 days</p>
                )}
              </div>
            </div>

            {/* SOP Quick Links */}
            <div className="bg-white rounded-sm border" style={{ borderColor: '#E5E1DB' }}>
              <div className="px-5 py-3" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
                  <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#B8860B' }}>Standard Operating Procedures</h2>
                </div>
              </div>
              <div className="p-4 space-y-2">
                {[
                  { title: 'Weekly CEO Review', desc: 'Mon AM: Review dashboard, check overdue, plan week', color: '#2563EB' },
                  { title: 'HMRC Filing Rhythm', desc: 'Check deadlines tab weekly. File 7 days before due date', color: '#D4AF37' },
                  { title: 'Debt Management Protocol', desc: 'TTP first, recall from collectors, document all calls', color: '#DC2626' },
                  { title: 'Email Triage', desc: 'Sweep for dates → add to planner → reply within 24h', color: '#0D9488' },
                  { title: 'Kids Appointments', desc: 'Book transport 5-7 days ahead. Confirm NHS + hospital numbers', color: '#EC4899' },
                  { title: 'Financial Close', desc: 'Month-end: reconcile Stripe, review P&L, update forecasts', color: '#8B5CF6' },
                ].map((sop, i) => (
                  <div key={i} className="px-3 py-2.5 rounded-sm" style={{ backgroundColor: '#FAF7F2', borderLeft: `3px solid ${sop.color}` }}>
                    <p className="text-xs font-medium" style={{ color: '#2C1A0E' }}>{sop.title}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: '#8B7355' }}>{sop.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Companies Overview */}
            <div className="bg-white rounded-sm border" style={{ borderColor: '#E5E1DB' }}>
              <div className="px-5 py-3" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
                  <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#B8860B' }}>Portfolio — {data.companies.count} Divisions</h2>
                </div>
              </div>
              <div className="p-4">
                <div className="flex flex-wrap gap-2">
                  {data.companies.names.map((name, i) => (
                    <span key={i} className="text-xs px-3 py-1.5 rounded-sm" style={{ backgroundColor: '#FAF7F2', color: '#2C1A0E', border: '1px solid #E5E1DB' }}>{name}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CeoDashboard;
